// The screens Costs and Quote to budget share: the field label and input style, the document preview beside the
// form, the "reading it" card and the drop / choose-a-file card.
import { h } from '../core/react.js';
import { MONO, T } from '../core/theme.js';
import { safeFileUrl } from '../core/util.js';
import { INV_MAX_MB } from '../core/doc-upload.js';
import { Icon } from './icons.js';

export const InvLabel = ({
  children
}) => h("div", {
  style: {
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: T.mist,
    marginBottom: 6
  }
}, children);
export const invInput = (mono, extra = {}) => ({
  ...(mono ? MONO : {}),
  width: '100%',
  padding: '9px 10px',
  border: `1px solid ${T.rule}`,
  background: T.ivory,
  fontSize: 12,
  color: T.coal,
  borderRadius: 0,
  fontFamily: mono ? "'DM Mono',monospace" : "'Open Sans',system-ui,sans-serif",
  ...extra
});
// The uploaded document, beside the form that is being checked against it; `empty` is shown when there is none
export function DocPreview({ file, what, basis = '38%', empty = null }) {
  const framed = file?.type === 'application/pdf' || file?.type === 'text/plain';
  return h("div", { style: { flex: '1 1 ' + basis, minWidth: 0, display: 'flex', flexDirection: 'column', background: T.ivoryD, borderRight: `1px solid ${T.rule}` } },
    h("div", { style: { padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.rule}`, background: T.white } },
      h("span", { style: { ...MONO, fontSize: 11, color: T.slate, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, file?.name),
      safeFileUrl(file?.url) && h("a", { href: safeFileUrl(file.url), target: "_blank", rel: "noopener noreferrer", style: { fontSize: 11, color: T.navyL, flexShrink: 0, marginLeft: 12 } }, "Open in new tab")),
    h("div", { style: { flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: framed ? 0 : 16 } },
      !file && empty ? empty : framed ? h("iframe", { src: file.url, title: what + " preview", style: { width: '100%', height: '100%', border: 'none', background: T.white } })
        : h("img", { src: file?.url, alt: what, style: { maxWidth: '100%', boxShadow: '0 2px 12px rgba(62,63,67,.12)' } })));
}
// While Claude reads the document
export function ReadingCard({ text, file }) {
  return h("div", { style: { border: `1px solid ${T.rule}`, background: T.ivory, padding: '34px 16px', textAlign: 'center' } },
    h("div", { style: { width: 18, height: 18, border: `2px solid ${T.ivoryDD}`, borderTopColor: T.navy, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin .8s linear infinite' } }),
    h("div", { style: { fontSize: 12.5, color: T.coal, fontWeight: 500 } }, text),
    h("div", { style: { ...MONO, fontSize: 10.5, color: T.mist, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, file?.name));
}
// The dashed "drop a document here / choose a file" card and its hidden file input. Phones can't drag and drop, so
// they are offered the file picker (which can take a photo).
export function DropCard({ icon, add, drop, dragOver, narrow, inputRef, onFile }) {
  return h(React.Fragment, null,
    h("button", { onClick: () => inputRef.current?.click(), style: { border: `1.5px dashed ${dragOver ? T.navy : T.ivoryDD}`, background: dragOver ? T.blueBg : T.ivory, padding: '34px 16px', textAlign: 'center', transition: 'all .15s', width: '100%' } },
      h("div", { style: { display: 'flex', justifyContent: 'center', marginBottom: 10 } }, h(icon, { s: 22, c: dragOver ? T.navy : T.mist })),
      h("div", { style: { fontSize: 12.5, color: T.coal, fontWeight: 500 } }, dragOver ? 'Drop to read it' : narrow ? add : drop),
      h("div", { style: { fontSize: 11.5, color: T.navyL, marginTop: 3 } }, narrow ? "Tap to choose a file or take a photo" : "or choose a file"),
      h("div", { style: { fontSize: 10.5, color: T.mist, marginTop: 10 } }, "PDF, JPG or PNG \xB7 up to ", INV_MAX_MB, " MB")),
    h("input", { ref: inputRef, type: "file", accept: ".pdf,image/jpeg,image/png,image/webp,image/gif", style: { display: 'none' }, onChange: e => { onFile(e.target.files?.[0]); e.target.value = ''; } }));
}
// No project open: say what the page is for and offer Project settings
export function NoProject({ text, setView }) {
  return h("div", { style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.ivory }, className: "fade-up" },
    h("div", { style: { maxWidth: 380, textAlign: 'center' } },
      h("div", { style: { fontSize: 16, fontWeight: 600, color: T.coal, marginBottom: 8 } }, "No project selected"),
      h("div", { style: { fontSize: 12.5, color: T.slate, lineHeight: 1.6, marginBottom: 20 } }, text),
      h("button", { onClick: () => setView('project'), style: { padding: '11px 20px', background: T.navy, color: T.white, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em' } }, "PROJECT & IMPORT")));
}
// The list's heading: its title, the project, and Refresh (spinning while loading)
export function ListHead({ title, sub, loading, onRefresh }) {
  return h("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 } },
    h("div", null,
      h("div", { style: { fontSize: 16, fontWeight: 600, color: T.coal, marginBottom: 2 } }, title),
      h("div", { style: { fontSize: 12, color: T.mist } }, sub)),
    h("button", { onClick: onRefresh, disabled: loading, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: T.slate, padding: '6px 10px', border: `1px solid ${T.rule}`, background: T.white } },
      h("span", { style: { display: 'flex', animation: loading ? 'spin 1s linear infinite' : 'none' } }, h(Icon.refresh, { s: 12, c: T.slate })), "Refresh"));
}
